import { useQuery } from "@tanstack/react-query";
import { ClinicScheduleStatusDto, SpecialtiesClient } from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { wrapFetchWithSignal } from "../utils/abort";
import { appConfig } from "../appconfig";

type ClinicsPagedPayload = {
  clinics?: ClinicScheduleStatusDto[];
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
  hasNextPage?: boolean;
};

export function useClinics(excludeConfigRoom?: boolean) {
  return useQuery({
    queryKey: ["clinics", 1, excludeConfigRoom],
    queryFn: async ({ signal }) => {
      const client = new SpecialtiesClient(appConfig.apiBaseUrl, {
        fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
      });
      const data = await client.getClinics(1, excludeConfigRoom);
      return data ?? [];
    },
    retry(failureCount, error: any) {
      if (error?.name === "AbortError") return false;
      return failureCount < 2;
    },
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function usePagedClinics({
  pageNumber,
  pageSize,
  searchTerm,
}: {
  pageNumber: number;
  pageSize: number;
  searchTerm: string;
}) {
  return useQuery({
    queryKey: ["clinics", "paged", pageNumber, pageSize, searchTerm],
    queryFn: async ({ signal }) => {
      const client = new SpecialtiesClient(appConfig.apiBaseUrl, {
        fetch: wrapFetchWithSignal(fetchWithTimeout(), signal),
      });

      const specialtyId = 1;
      const term = searchTerm.trim() ? searchTerm.trim() : undefined;

      try {
        const payload = await client.getClinicsPaged(
          specialtyId,
          pageNumber,
          pageSize,
          term,
        );

        const p = payload ?? {};

        return {
          clinics: p.clinics ?? [],
          totalCount: p.totalCount ?? 0,
          pageNumber: p.pageNumber ?? pageNumber,
          pageSize: p.pageSize ?? pageSize,
          hasNextPage: p.hasNextPage ?? false,
        };
      } catch (e) {
        console.error("getClinicsPaged error:", e);
        throw e;
      }
    },
    placeholderData: (prev: any) => prev,
    retry(failureCount: any, error: any) {
      if (error?.name === "AbortError") return false;
      return failureCount < 2;
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}
